import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  ChangeDetectionStrategy,
  input,
  afterRenderEffect,
} from '@angular/core';

import * as SignaturePadNative from './signature_pad';
import { DataUri, unsafeDataUri } from '@softline/core';

export interface Point {
  x: number;
  y: number;
  time: number;
}

export type PointGroup = Point[];

@Component({
  selector: 'soft-signature-pad',
  template: `
    <div
      class="absolute inset-2 flex items-center justify-center mx-auto my-auto select-none pointer-events-none"
    >
      <ng-content></ng-content>
    </div>
    <canvas></canvas>
  `,
  styleUrls: ['signature-pad.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignaturePadComponent implements AfterContentInit, OnDestroy {
  private signaturePad: any;
  private elementRef: ElementRef;

  @Input() options: any;

  readonly canvasHeight = input<string>();
  readonly canvasWidth = input<string>();

  @Output() onBeginEvent: EventEmitter<boolean>;
  @Output() onEndEvent: EventEmitter<boolean>;

  constructor(elementRef: ElementRef) {
    // no op
    this.elementRef = elementRef;
    this.options = this.options || {};
    this.onBeginEvent = new EventEmitter();
    this.onEndEvent = new EventEmitter();

    // Apply a bound size only when numeric and changed: `undefined` or '100%' would
    // zero the canvas, and any write to width/height wipes the bitmap.
    afterRenderEffect(() => {
      const width = Number(this.canvasWidth());
      const height = Number(this.canvasHeight());
      const canvas: HTMLCanvasElement | null =
        this.elementRef.nativeElement.querySelector('canvas');
      if (!canvas) return;
      if (!Number.isNaN(width) && canvas.width !== width) canvas.width = width;
      if (!Number.isNaN(height) && canvas.height !== height) canvas.height = height;
    });
  }

  ngAfterContentInit(): void {
    const canvas: any = this.elementRef.nativeElement.querySelector('canvas');

    if ((this.options as any).canvasHeight) {
      canvas.height = (this.options as any).canvasHeight;
    }

    if ((this.options as any).canvasWidth) {
      canvas.width = (this.options as any).canvasWidth;
    }

    this.signaturePad = new SignaturePadNative.default(canvas, this.options);
    this.signaturePad.onBegin = this.onBegin.bind(this);
    this.signaturePad.onEnd = this.onEnd.bind(this);
  }

  ngOnDestroy(): void {
    // signature_pad registers pointerup/mouseup on `document`; without off() every
    // destroyed pad (and its full-size canvas bitmap) stays reachable.
    this.signaturePad?.off();
    const canvas: HTMLCanvasElement | null =
      this.elementRef.nativeElement.querySelector('canvas');
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }

  resizeCanvas(): void {
    // When zoomed out to less than 100%, for some very strange reason,
    // some browsers report devicePixelRatio as less than 1
    // and only part of the canvas is cleared then.
    const ratio: number = Math.max(window.devicePixelRatio || 1, 1);
    const canvas: any = this.signaturePad.canvas;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value
  }

  // Returns signature image as an array of point groups
  toData(): PointGroup[] {
    if (this.signaturePad) {
      return this.signaturePad.toData();
    } else {
      return [];
    }
  }

  // Draws signature image from an array of point groups
  fromData(points: PointGroup[]): void {
    this.signaturePad.fromData(points as any);
  }

  // Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible paramters)
  toDataUri(imageType?: string, quality?: number): DataUri {
    return unsafeDataUri(this.signaturePad.toDataUri(imageType, quality)); // save image as data URL
  }

  // Draws signature image from data URL
  fromDataURL(dataURL: string, options: any = {}): void {
    // set default height and width on read data from URL
    if (!options.hasOwnProperty('height') && (this.options as any).canvasHeight) {
      options.height = (this.options as any).canvasHeight;
    }
    if (!options.hasOwnProperty('width') && (this.options as any).canvasWidth) {
      options.width = (this.options as any).canvasWidth;
    }
    this.signaturePad.fromDataURL(dataURL, options);
  }

  // Clears the canvas
  clear(): void {
    this.signaturePad.clear();
  }

  // Returns true if canvas is empty, otherwise returns false
  isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  // Unbinds all event handlers
  off(): void {
    this.signaturePad.off();
  }

  // Rebinds all event handlers
  on(): void {
    this.signaturePad.on();
  }

  // set an option on the signaturePad - e.g. set('minWidth', 50);
  set(option: string, value: any): void {
    switch (option) {
      case 'canvasHeight':
        this.signaturePad.canvas.height = value;
        break;
      case 'canvasWidth':
        this.signaturePad.canvas.width = value;
        break;
      default:
        this.signaturePad[option] = value;
    }
  }

  // notify subscribers on signature begin
  onBegin(): void {
    this.onBeginEvent.emit(true);
  }

  // notify subscribers on signature end
  onEnd(): void {
    this.onEndEvent.emit(true);
  }

  queryPad(): any {
    return this.signaturePad;
  }
}
